import { IonButton } from '@ionic/react';
import { FieldArray, FormikProvider } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { defaultTaxWithhold, isDocTypeCodeValidForTaxWithhold } from '../../../config/utils/salesHelper';
import { emptyProductObject } from '../../../services/utils/data';
import { isDocTypeStandardDeliveryNote } from '../../../services/utils/myDataUtils';
import { BToggle } from '../BToggle';
import { DocTaxWithhold } from './DocTaxWithhold';
import { DocProduct } from './product/DocProduct';
import { DocProductsCost } from './product/DocProductsCost';

interface Props {
  field: any;
  formik: any;
  isSectionDisabled?: boolean;
  taxWithholdField: any;
  productsToBeWeighedField: any;
}

export const DocProducts = ({
  formik,
  field,
  isSectionDisabled = false,
  taxWithholdField,
  productsToBeWeighedField,
}: Props) => {
  const { t } = useTranslation();
  const [debugMode, setDebugMode] = useState(false);
  const tapCount = useRef(0);
  const tapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const docTypeAadeCode = useMemo(
    () => formik?.values?.['documentTypeId']?.aadeCode || formik?.values?.['documentTypeId']?.object?.aadeCode,
    [formik?.values?.['documentTypeId']],
  );

  useEffect(() => {
    if (isDocTypeCodeValidForTaxWithhold(docTypeAadeCode)) {
      if (!taxWithholdField.value) {
        formik?.setFieldValue(taxWithholdField.name, defaultTaxWithhold(taxWithholdField.data)[0]);
      }
      return;
    }
    formik?.setFieldValue(taxWithholdField.name, null);
  }, [docTypeAadeCode]);

  const handleDebugTap = useCallback(() => {
    tapCount.current += 1;
    if (tapTimer.current) clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => {
      tapCount.current = 0;
    }, 1000);
    if (tapCount.current >= 5) {
      tapCount.current = 0;
      if (tapTimer.current) clearTimeout(tapTimer.current);
      setDebugMode((prev) => !prev);
    }
  }, []);

  const getProducts = useCallback(() => {
    return formik.values?.products?.map((prod: any, index: number) => (
      <DocProduct
        formik={formik}
        key={index}
        index={index}
        isSectionDisabled={isSectionDisabled}
        docTypeAadeCode={docTypeAadeCode}
        debugMode={debugMode}
      />
    ));
  }, [debugMode, docTypeAadeCode, formik.values?.products, isSectionDisabled]);

  const getTaxWithholdSection = useCallback(() => {
    if (isDocTypeCodeValidForTaxWithhold(docTypeAadeCode)) {
      return <DocTaxWithhold formik={formik} taxWithholdField={taxWithholdField} />;
    }
  }, [formik, taxWithholdField]);

  const getProductsToBeWeighedToggle = useCallback(() => {
    if (isDocTypeStandardDeliveryNote(docTypeAadeCode)) {
      return <BToggle field={productsToBeWeighedField} formik={formik} marginTop="margin-t-0" />;
    }
  }, [docTypeAadeCode, formik, productsToBeWeighedField]);

  return (
    <div style={{ minHeight: 400 }} className="docForm padding-t-16 padding-b-16">
      <div className="margin-b-16 padding-l-16 flex-start-center-row-wrap gap-20">
        <div className="mediumText6" onClick={handleDebugTap} style={{ userSelect: 'none' }}>
          {t('SALE.PRODUCTS')} / {t('SALE.SERVICES')}
        </div>
        {getProductsToBeWeighedToggle()}
      </div>
      <div className="gap-10">{getProducts()}</div>
      <div className="margin-t-20 padding-l-16 padding-r-16 flex-space-between-start-row-wrap">
        <div>
          <FormikProvider value={formik}>
            <FieldArray
              name="products"
              render={({ push }) => (
                <IonButton
                  mode="md"
                  disabled={isSectionDisabled}
                  onClick={() => push(emptyProductObject)}
                  className="button-focused">
                  {t('SALE.ADD_ROW')}
                </IonButton>
              )}
            />
          </FormikProvider>
          {getTaxWithholdSection()}
        </div>
        <DocProductsCost formik={formik} />
      </div>
    </div>
  );
};
